import { IconBook as BookOpenCheck, IconFlask as FlaskConical, IconRefresh as RefreshCw } from "./icons";
import { DayHeading, DaySection } from "./day";

function Tag({ tone, children }: { tone: "proven" | "promising" | "open"; children: string }) {
  const styles = {
    proven: "bg-[#E3F4D6] text-[#3E7A22]",
    promising: "bg-[#FFF1C7] text-[#8A6100]",
    open: "bg-[#EFEAFF] text-[#5B3FD9]",
  }[tone];
  return <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-extrabold whitespace-nowrap ${styles}`}>{children}</span>;
}

export function Research() {
  return (
    <DaySection id="research" label="Research and trust">
      <DayHeading
        eyebrow="Trust"
        title="Up to date, and backed by sources you can check."
        intro="Some topics change every month. Some have to be exactly right. LearnAnything works out which yours is and does the homework, while your first lesson is already underway."
      />

      <div className="mt-14 grid gap-5 lg:grid-cols-2">
        {/* Frontier */}
        <div className="rounded-[22px] border-2 border-b-[6px] border-[#E7E3F0] bg-white p-5 sm:p-8">
          <div className="flex items-center gap-3">
            <span className="grid size-10 shrink-0 place-items-center rounded-xl border-b-[3px] border-[#4128B8] bg-[#6B4EFF] text-white">
              <FlaskConical className="size-5" />
            </span>
            <h3 className="text-xl font-black tracking-tight text-[#1C1A27]">The latest, labelled honestly</h3>
          </div>
          <p className="mt-3 leading-relaxed text-[#6B6780]">
            In fast-moving fields you learn what was found this year, marked proven or promising, right up to the open
            questions you could work on yourself.
          </p>
          <ul className="mt-6 space-y-3">
            {[
              { t: "Fungal networks carry electrical spikes", tag: <Tag tone="proven">Proven</Tag> },
              { t: "Fungal tissue can act as a memristor", tag: <Tag tone="promising">Promising</Tag> },
              { t: "Can a fungal network learn a task on its own?", tag: <Tag tone="open">Open problem</Tag> },
            ].map(r => (
              <li key={r.t} className="flex items-center justify-between gap-3 rounded-2xl bg-[#F7F5FF] px-4 py-3">
                <span className="text-sm font-bold text-[#1C1A27] sm:text-[15px]">{r.t}</span>
                {r.tag}
              </li>
            ))}
          </ul>
          <div className="mt-4 flex gap-3 rounded-2xl border-2 border-dashed border-[#CFC4FF] px-4 py-3">
            <RefreshCw className="mt-0.5 size-4 shrink-0 text-[#6B4EFF]" />
            <p className="text-sm leading-relaxed">
              <span className="font-extrabold text-[#1C1A27]">Update to Lesson 3.</span>{" "}
              <span className="text-[#6B6780]">
                Research found newer results on fungal memory. Here's what changed, and why.
              </span>
            </p>
          </div>
        </div>

        {/* Grounded */}
        <div className="rounded-[22px] border-2 border-b-[6px] border-[#E7E3F0] bg-white p-5 sm:p-8">
          <div className="flex items-center gap-3">
            <span className="grid size-10 shrink-0 place-items-center rounded-xl border-b-[3px] border-[#4128B8] bg-[#6B4EFF] text-white">
              <BookOpenCheck className="size-5" />
            </span>
            <h3 className="text-xl font-black tracking-tight text-[#1C1A27]">Your field’s own sources</h3>
          </div>
          <p className="mt-3 leading-relaxed text-[#6B6780]">
            Training for a profession? Lessons follow your field’s official guidance for your region, with a citation on
            every key claim. Topics where mistakes matter wait for their sources instead of guessing.
          </p>
          <div className="mt-6 rounded-2xl bg-[#F7F5FF] p-4">
            <p className="text-xs font-extrabold uppercase tracking-wider text-[#6B6780]">
              Counselling trainee · sources for this course
            </p>
            <ul className="mt-3 space-y-2 text-sm font-bold text-[#1C1A27]">
              {[
                "Your regulator's code of ethics",
                "Standards of practice for your province",
                "Entry-to-practice competency profile",
                "Peer-reviewed research on informed consent",
              ].map((s, i) => (
                <li key={s} className="flex items-center gap-3">
                  <span className="grid size-6 shrink-0 place-items-center rounded-md bg-[#6B4EFF] text-xs font-extrabold text-white">
                    {i + 1}
                  </span>
                  {s}
                </li>
              ))}
            </ul>
          </div>
          <p className="mt-4 rounded-2xl border-2 border-[#E7E3F0] px-4 py-3 text-sm leading-relaxed font-bold text-[#1C1A27]">
            “Before sharing anything a client tells you, you need their informed consent, with a few narrow exceptions.
            <sup className="ml-0.5 rounded bg-[#FFF1C7] px-1 font-extrabold text-[#8A6100]">1</sup>”
            <span className="mt-1 block text-[#6B6780]">Tap any key claim to see where it came from.</span>
          </p>
        </div>
      </div>
    </DaySection>
  );
}
